/**
 * Methods the loader stub in `utils/pendo.js` queues until the Pendo agent script loads. If that script is blocked,
 * `window.pendo` stays the stub for the whole session.
 */
type PendoStub = Record<
  'initialize' | 'identify' | 'updateOptions' | 'pageLoad' | 'track',
  (...args: unknown[]) => void
>;

/**
 * Methods only the loaded agent has.
 */
interface PendoAgent {
  showGuideById: (guideId: string) => boolean;
  onGuideDismissed: () => void;
}

/**
 * Agent methods are optional because `window.pendo` may still be the stub, so callers must check for them.
 */
export type PendoAPI = PendoStub & Partial<PendoAgent>;

/**
 * Extends the global `window` object with Pendo.
 */
declare global {
  interface Window {
    pendo?: PendoAPI;
  }
}

/**
 * Getter function for accessing Pendo API safely.
 * Ensures that `window.pendo` is always accessed at runtime,
 * even if Pendo initializes after this module is loaded.
 */
const getPendoAPI = (): PendoAPI | undefined => {
  return window.pendo;
};

export default getPendoAPI;
